import React from 'react';
import { cn } from '@/lib/utils';
import { Building2, type LucideIcon, CirclePlus } from 'lucide-react';
import { Button } from '@/components/Button';
import { PermissionGuardBase } from '@/components/PermissionGuardBase';

export interface TableEmptyStateStyles {
  emptyState: string;
  emptyStateTitle: string;
  emptyStateMessage: string;
  iconCluster: string;
  iconCenter: string;
  iconTop: string;
  iconBottom: string;
  emptyStateBtn?: string;
  [key: string]: string | undefined;
}

export interface TableEmptyStateProps extends React.HTMLAttributes<HTMLDivElement> {
  imageSrc?: string;
  imageAlt?: string;
  /** Primary icon shown in the center card */
  Icon?: LucideIcon;
  /** Top-right floating card icon. Falls back to default decorative bars when omitted. */
  secondaryIconTop?: LucideIcon;
  /** Bottom-left floating card icon. Falls back to default decorative SVG when omitted. */
  secondaryIconBottom?: LucideIcon;
  title?: string;
  message?: string;
  ctaLabel?: string;
  onCtaClick?: () => void;
  size?: 'sm' | 'md';
  /** When provided alongside `permissions`, the CTA is hidden unless the scope is present. */
  permissionScope?: string | string[];
  permissions?: Set<unknown>;
}

/**
 * Factory that creates a TableEmptyState component bound to a specific CSS module.
 * Each app passes its own styles so branding is independent while logic stays centralised.
 *
 * Usage:
 *   import styles from './TableEmptyState.module.css';
 *   import { createTableEmptyState } from '@gokrew/ui';
 *   export default createTableEmptyState(styles);
 */
export function createTableEmptyState(styles: TableEmptyStateStyles) {
  function DefaultTopDecor() {
    return (
      <div className="flex flex-col gap-[0.3125rem] w-[1.5rem]">
        <div className="h-[0.3125rem] rounded-sm bg-(--brand-empty-decor-primary) w-full" />
        <div className="h-[0.3125rem] rounded-sm bg-(--brand-empty-decor-primary) opacity-75 w-3/4" />
        <div className="h-[0.3125rem] rounded-sm bg-(--brand-empty-decor-primary) opacity-50 w-1/2" />
      </div>
    );
  }

  function DefaultBottomDecor() {
    return (
      <svg
        viewBox="0 0 24 24"
        className="w-[1.5rem] h-[1.5rem] text-(--brand-empty-decor-secondary)"
        fill="none"
      >
        <circle cx="12" cy="12" r="2.5" fill="currentColor" />
        <circle cx="5" cy="7" r="2" fill="currentColor" opacity=".6" />
        <circle cx="19" cy="7" r="2" fill="currentColor" opacity=".6" />
        <circle cx="5" cy="17" r="2" fill="currentColor" opacity=".6" />
        <circle cx="19" cy="17" r="2" fill="currentColor" opacity=".6" />
        <line
          x1="12"
          y1="12"
          x2="5"
          y2="7"
          stroke="currentColor"
          strokeWidth="1.2"
          opacity=".5"
        />
        <line
          x1="12"
          y1="12"
          x2="19"
          y2="7"
          stroke="currentColor"
          strokeWidth="1.2"
          opacity=".5"
        />
        <line
          x1="12"
          y1="12"
          x2="5"
          y2="17"
          stroke="currentColor"
          strokeWidth="1.2"
          opacity=".5"
        />
        <line
          x1="12"
          y1="12"
          x2="19"
          y2="17"
          stroke="currentColor"
          strokeWidth="1.2"
          opacity=".5"
        />
      </svg>
    );
  }

  return function TableEmptyState({
    className,
    imageSrc,
    imageAlt = 'empty state illustration',
    Icon = Building2,
    secondaryIconTop: TopIcon,
    secondaryIconBottom: BottomIcon,
    title = 'Nothing found',
    message = "Your organization's tree is currently empty.",
    ctaLabel = 'Create your first',
    onCtaClick,
    size = 'md',
    permissionScope,
    permissions,
  }: TableEmptyStateProps) {
    const cta = onCtaClick && (
      <Button
        variant="primary"
        size={size}
        IconPrefix={CirclePlus}
        onClick={onCtaClick}
        className={styles.emptyStateBtn}
      >
        {ctaLabel}
      </Button>
    );

    return (
      <div
        className={cn(
          styles.emptyState,
          styles[`emptyState${size.toUpperCase()[0]}${size.slice(1)}`],
          className
        )}
      >
        <div className={styles.iconCluster}>
          <div className={styles.iconCenter}>
            {imageSrc ? (
              <img
                src={imageSrc}
                alt={imageAlt}
                className="w-[3.5rem] h-[3.5rem] object-contain"
              />
            ) : (
              <Icon className="w-[3.5rem] h-[3.5rem]" strokeWidth={1.5} />
            )}
          </div>

          <div className={styles.iconTop}>
            {TopIcon ? (
              <TopIcon size={18} strokeWidth={1.5} />
            ) : (
              <DefaultTopDecor />
            )}
          </div>

          <div className={styles.iconBottom}>
            {BottomIcon ? (
              <BottomIcon size={18} strokeWidth={1.5} />
            ) : (
              <DefaultBottomDecor />
            )}
          </div>
        </div>

        <h3 className={styles.emptyStateTitle}>{title}</h3>
        <p className={styles.emptyStateMessage}>{message}</p>

        {permissionScope && permissions ? (
          <PermissionGuardBase
            scope={permissionScope}
            permissions={permissions}
          >
            {cta}
          </PermissionGuardBase>
        ) : (
          cta
        )}
      </div>
    );
  };
}
